import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Plus, Trash2, RefreshCw, Users, Megaphone } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { usePermissions } from '../../hooks/usePermissions'
import { API_CONFIG } from '../../config/api.config'

const base = API_CONFIG.python.apiUrl
const hdr = () => {
  const t = localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || ''
  return { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' }
}

interface KW { keyword: string; slug: string; title: string; description?: string; article_url?: string; driver_url?: string; messenger_text?: string; active: boolean; claims: number }
interface Lead { email?: string; name?: string; keyword?: string; slug?: string; stage?: string; created_at?: string; claimed_at?: string }

const empty: KW = { keyword: '', slug: '', title: '', description: '', article_url: '', driver_url: '', messenger_text: '', active: true, claims: 0 }

const FbCampaignManager: React.FC = () => {
  const { isAuthenticated } = useAuth()
  const { isAdmin } = usePermissions()
  const [kws, setKws] = useState<KW[]>([])
  const [leads, setLeads] = useState<Lead[]>([])
  const [form, setForm] = useState<KW>(empty)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')

  const load = () => {
    fetch(`${base}/fb/admin/keywords`, { headers: hdr() }).then((r) => (r.ok ? r.json() : [])).then(setKws).catch(() => {})
    fetch(`${base}/fb/admin/leads`, { headers: hdr() }).then((r) => (r.ok ? r.json() : [])).then(setLeads).catch(() => {})
  }
  useEffect(() => { if (isAdmin) load() }, [isAdmin])

  if (!isAuthenticated || !isAdmin) {
    return <div className="p-10 text-center text-gray-500">Chỉ dành cho quản trị viên. <Link to="/" className="text-blue-600 font-bold">Về trang chủ</Link></div>
  }

  const save = async () => {
    if (!form.keyword || !form.slug || !form.title) { setMsg('Cần keyword, slug, title'); return }
    setBusy(true); setMsg('')
    try {
      const r = await fetch(`${base}/fb/admin/keywords`, { method: 'POST', headers: hdr(), body: JSON.stringify({ ...form, keyword: form.keyword.toUpperCase() }) })
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || 'Lỗi lưu')
      setForm(empty); setMsg('Đã lưu ✓'); load()
    } catch (e) { setMsg((e as Error).message) } finally { setBusy(false) }
  }
  const del = async (slug: string) => {
    if (!confirm(`Xoá từ khoá "${slug}"?`)) return
    await fetch(`${base}/fb/admin/keywords/${slug}`, { method: 'DELETE', headers: hdr() }); load()
  }

  const inp = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200'

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <div className="flex items-center gap-3">
          <span className="grid h-11 w-11 place-items-center rounded-xl bg-blue-600 text-white"><Megaphone size={22} /></span>
          <div>
            <h1 className="text-2xl font-black text-slate-900">Chiến dịch Facebook · Quà theo comment</h1>
            <p className="text-sm text-slate-500">Comment đúng từ khoá → tự gửi Messenger → đăng nhập nhận quà → thu lead.</p>
          </div>
          <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-100"><RefreshCw size={15} /> Tải lại</button>
        </div>

        {/* Form thêm/sửa từ khoá */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-black text-slate-800"><Plus size={18} className="text-blue-600" /> Thêm / cập nhật từ khoá (theo slug)</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <input className={inp} placeholder="Từ khoá comment (VD: OPENCV)" value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} />
            <input className={inp} placeholder="Slug landing (VD: opencv)" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
            <input className={`${inp} sm:col-span-2`} placeholder="Tiêu đề quà" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <textarea className={`${inp} sm:col-span-2`} rows={2} placeholder="Mô tả (hiện trước khi đăng nhập)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <input className={inp} placeholder="Link bài viết (article_url, công khai)" value={form.article_url} onChange={(e) => setForm({ ...form, article_url: e.target.value })} />
            <input className={inp} placeholder="Link tải (driver_url, chỉ sau đăng nhập)" value={form.driver_url} onChange={(e) => setForm({ ...form, driver_url: e.target.value })} />
            <textarea className={`${inp} sm:col-span-2`} rows={2} placeholder="Nội dung tin nhắn Messenger (để trống = mặc định)" value={form.messenger_text} onChange={(e) => setForm({ ...form, messenger_text: e.target.value })} />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm font-bold text-slate-600"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Kích hoạt</label>
            <button onClick={save} disabled={busy} className="ml-auto inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-black text-white hover:bg-blue-700 disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Lưu</button>
            {msg && <span className="text-sm font-bold text-emerald-600">{msg}</span>}
          </div>
        </div>

        {/* Danh sách từ khoá */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="mb-3 font-black text-slate-800">Từ khoá ({kws.length})</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b"><th className="py-2 pr-2">Từ khoá</th><th className="py-2 pr-2">Slug</th><th className="py-2 pr-2">Tiêu đề</th><th className="py-2 px-2 text-center">Nhận</th><th className="py-2 px-2 text-center">Bật</th><th></th></tr></thead>
              <tbody>
                {kws.map((k) => (
                  <tr key={k.slug} className="border-b border-slate-50">
                    <td className="py-2 pr-2 font-mono font-bold text-blue-700">{k.keyword}</td>
                    <td className="py-2 pr-2 font-mono text-slate-500">{k.slug}</td>
                    <td className="py-2 pr-2">{k.title}</td>
                    <td className="py-2 px-2 text-center font-bold">{k.claims}</td>
                    <td className="py-2 px-2 text-center">{k.active ? '✅' : '—'}</td>
                    <td className="py-2 px-2 text-right">
                      <button onClick={() => setForm(k)} className="mr-2 text-xs font-bold text-blue-600 hover:underline">Sửa</button>
                      <button onClick={() => del(k.slug)} className="text-rose-500 hover:text-rose-700"><Trash2 size={15} /></button>
                    </td>
                  </tr>
                ))}
                {!kws.length && <tr><td colSpan={6} className="py-6 text-center text-slate-400">Chưa có từ khoá. Thêm ở trên.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Leads */}
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 font-black text-slate-800"><Users size={18} className="text-emerald-600" /> Khách hàng thu được ({leads.length})</h2>
          <div className="max-h-96 overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white"><tr className="text-left text-slate-500 border-b"><th className="py-2 pr-2">Email</th><th className="py-2 pr-2">Tên</th><th className="py-2 pr-2">Từ khoá</th><th className="py-2 pr-2">Trạng thái</th><th className="py-2 pr-2">Thời gian</th></tr></thead>
              <tbody>
                {leads.map((l, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-2 pr-2 font-semibold text-slate-800">{l.email || <span className="text-slate-400">(chưa đăng nhập)</span>}</td>
                    <td className="py-2 pr-2 text-slate-600">{l.name || '—'}</td>
                    <td className="py-2 pr-2 font-mono text-blue-700">{l.keyword || l.slug}</td>
                    <td className="py-2 pr-2">{l.stage === 'claimed' ? <span className="text-emerald-600 font-bold">Đã nhận</span> : <span className="text-amber-600">Đã nhắn</span>}</td>
                    <td className="py-2 pr-2 text-xs text-slate-400">{(l.claimed_at || l.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
                {!leads.length && <tr><td colSpan={5} className="py-6 text-center text-slate-400">Chưa có lead.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FbCampaignManager
